import { Link } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import {
  usePersonalBudgets,
  type BudgetWithSpent,
} from '@/hooks';
import { useSavingsGoals } from '@/hooks';
import { usePersonalTransactions } from '@/hooks';
import { usePersonalLoans } from '@/hooks';
import {
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
  PiggyBank,
  BarChart3,
  FileBarChart,
  Plus,
  FolderOpen,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Banknote,
  CreditCard,
} from 'lucide-react';

function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function getMonthRange(): { dateFrom: string; dateTo: string; monthLabel: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  const monthLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return { dateFrom, dateTo, monthLabel };
}

type BudgetStatus = 'on-track' | 'warning' | 'over';

function getBudgetStatus(budget: BudgetWithSpent): BudgetStatus {
  const limit = parseFloat(budget.limit_amount);
  if (limit <= 0) return 'on-track';
  const percentage = (budget.spent_amount / limit) * 100;
  if (percentage >= budget.alert_threshold_critical) return 'over';
  if (percentage >= budget.alert_threshold_warning) return 'warning';
  return 'on-track';
}

function getStatusColor(status: BudgetStatus): string {
  switch (status) {
    case 'over': return 'bg-sapling-error-100';
    case 'warning': return 'bg-sapling-warning-100';
    case 'on-track': return 'bg-sapling-primary-100';
  }
}

function getStatusTextColor(status: BudgetStatus): string {
  switch (status) {
    case 'over': return 'text-sapling-error-100';
    case 'warning': return 'text-sapling-warning-100';
    case 'on-track': return 'text-sapling-primary-100';
  }
}

function isPaymentFulfilled(
  paymentDueDay: number | null,
  lastPaymentDate: string | null
): boolean {
  if (!paymentDueDay || !lastPaymentDate) return false;
  const today = new Date();
  const lastPayment = new Date(lastPaymentDate + 'T00:00:00');
  const thisMonthDue = new Date(today.getFullYear(), today.getMonth(), paymentDueDay);
  const cycleStart =
    today < thisMonthDue
      ? new Date(today.getFullYear(), today.getMonth() - 1, paymentDueDay)
      : thisMonthDue;
  return lastPayment >= cycleStart;
}

export function PersonalDashboardPage() {
  const { dateFrom, dateTo, monthLabel } = getMonthRange();

  const { budgets, isLoading: budgetsLoading } = usePersonalBudgets();
  const { goals, isLoading: goalsLoading } = useSavingsGoals();
  const { transactions, isLoading: txLoading } = usePersonalTransactions({
    dateFrom,
    dateTo,
    limit: 100,
  });
  const { loans, isLoading: loansLoading } = usePersonalLoans();

  const isLoading = budgetsLoading || goalsLoading || txLoading || loansLoading;

  // Monthly summary
  const monthlyIncome = transactions
    .filter(t => t.transaction_type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
  const monthlyExpenses = transactions
    .filter(t => t.transaction_type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
  const monthlyLeft = monthlyIncome - monthlyExpenses;

  // Budget status counts
  const budgetStatuses = budgets.map(b => getBudgetStatus(b));
  const onTrackCount = budgetStatuses.filter(s => s === 'on-track').length;
  const warningCount = budgetStatuses.filter(s => s === 'warning').length;
  const overCount = budgetStatuses.filter(s => s === 'over').length;

  // Active savings goals
  const activeGoals = goals.filter(g => g.status === 'active');
  const topGoals = activeGoals.slice(0, 3);
  const totalSaved = activeGoals.reduce((sum, g) => sum + parseFloat(g.current_amount), 0);
  const totalTarget = activeGoals.reduce((sum, g) => sum + parseFloat(g.target_amount), 0);

  // Recent transactions (last 5 from current month, sorted by date desc)
  const recentTransactions = [...transactions]
    .sort((a, b) => b.transaction_date.localeCompare(a.transaction_date) || b.created_at.localeCompare(a.created_at))
    .slice(0, 5);

  // Upcoming loan payments (active loans sorted by next due date, top 5)
  const activeLoans = loans.filter(l => l.status === 'active');
  const upcomingLoans = [...activeLoans]
    .filter(l => l.payment_due_day !== null)
    .sort((a, b) => {
      const today = new Date();
      const currentDay = today.getDate();
      const aDue = a.payment_due_day!;
      const bDue = b.payment_due_day!;
      // Days until due (negative means past due this month)
      const aDaysUntil = aDue >= currentDay ? aDue - currentDay : aDue + 31 - currentDay;
      const bDaysUntil = bDue >= currentDay ? bDue - currentDay : bDue + 31 - currentDay;
      return aDaysUntil - bDaysUntil;
    })
    .slice(0, 5);
  const totalDebt = activeLoans.reduce((sum, l) => sum + parseFloat(l.current_balance), 0);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container">
          {/* Page header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold sm:text-3xl">
                Personal Finance
              </h1>
              <p className="mt-1 text-muted-foreground">{monthLabel} Overview</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/app/personal/transactions"
                search={{ action: 'add' } as Record<string, string>}
                className="inline-flex items-center gap-2 rounded-btn bg-sapling-primary-100 px-4 py-2 text-sm font-medium text-white hover:bg-sapling-primary-200 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Transaction
              </Link>
              <Link
                to="/app/personal/budgets"
                search={{ action: 'add' } as Record<string, string>}
                className="inline-flex items-center gap-2 rounded-btn border border-sapling-primary-100 px-4 py-2 text-sm font-medium text-sapling-primary-100 hover:bg-sapling-primary-100/10 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Budget
              </Link>
              <Link
                to="/app/personal/savings-goals"
                search={{ action: 'add' } as Record<string, string>}
                className="inline-flex items-center gap-2 rounded-btn border border-sapling-primary-100 px-4 py-2 text-sm font-medium text-sapling-primary-100 hover:bg-sapling-primary-100/10 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Goal
              </Link>
              <Link
                to="/app/personal/loans"
                search={{ action: 'add' } as Record<string, string>}
                className="inline-flex items-center gap-2 rounded-btn border border-sapling-primary-100 px-4 py-2 text-sm font-medium text-sapling-primary-100 hover:bg-sapling-primary-100/10 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Loan
              </Link>
            </div>
          </div>

          {isLoading ? (
            <div className="mt-12 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {/* This Month Card */}
                <div className="rounded-card border bg-card p-5">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Wallet className="h-4 w-4" />
                    This Month
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <TrendingUp className="h-3.5 w-3.5 text-sapling-primary-100" />
                        Income
                      </span>
                      <span className="font-medium text-sapling-primary-100">
                        {formatCurrency(monthlyIncome)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <TrendingDown className="h-3.5 w-3.5 text-sapling-error-100" />
                        Spent
                      </span>
                      <span className="font-medium text-sapling-error-100">
                        {formatCurrency(monthlyExpenses)}
                      </span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Remaining</span>
                        <span className={`font-semibold ${monthlyLeft >= 0 ? 'text-sapling-primary-100' : 'text-sapling-error-100'}`}>
                          {formatCurrency(monthlyLeft)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Budget Status Card */}
                <div className="rounded-card border bg-card p-5">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <BarChart3 className="h-4 w-4" />
                    Budget Status
                  </div>
                  {budgets.length === 0 ? (
                    <div className="mt-3 text-sm text-muted-foreground">
                      No budgets set up yet.{' '}
                      <Link to="/app/personal/budgets" className="text-sapling-primary-100 hover:underline">
                        Create one
                      </Link>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-sapling-primary-100" />
                        <span className="text-lg font-semibold">{onTrackCount}</span>
                        <span className="text-xs text-muted-foreground">On Track</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="h-4 w-4 text-sapling-warning-100" />
                        <span className="text-lg font-semibold">{warningCount}</span>
                        <span className="text-xs text-muted-foreground">Warning</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-sapling-error-100" />
                        <span className="text-lg font-semibold">{overCount}</span>
                        <span className="text-xs text-muted-foreground">Over</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Savings Progress Card */}
                <div className="rounded-card border bg-card p-5">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <PiggyBank className="h-4 w-4" />
                    Savings Progress
                  </div>
                  {activeGoals.length === 0 ? (
                    <div className="mt-3 text-sm text-muted-foreground">
                      No active goals.{' '}
                      <Link to="/app/personal/savings-goals" className="text-sapling-primary-100 hover:underline">
                        Set a goal
                      </Link>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {topGoals.map(goal => {
                        const current = parseFloat(goal.current_amount);
                        const target = parseFloat(goal.target_amount);
                        const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0;
                        return (
                          <div key={goal.id} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="truncate font-medium">{goal.name}</span>
                              <span className="text-muted-foreground">{Math.round(pct)}%</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted">
                              <div
                                className="h-1.5 rounded-full bg-sapling-primary-100 transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {activeGoals.length > 3 && (
                        <p className="text-xs text-muted-foreground">
                          +{activeGoals.length - 3} more goal{activeGoals.length - 3 > 1 ? 's' : ''}
                        </p>
                      )}
                      <div className="border-t pt-2 text-xs text-muted-foreground">
                        Total: {formatCurrency(totalSaved)} of {formatCurrency(totalTarget)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Budget Overview */}
              <section className="mt-8">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-lg font-semibold">Budget Overview</h2>
                  <Link
                    to="/app/personal/budgets"
                    className="flex items-center gap-1 text-sm text-sapling-primary-100 hover:underline"
                  >
                    Manage Budgets <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                {budgets.length === 0 ? (
                  <div className="mt-4 rounded-card border border-dashed bg-muted/50 p-6 text-center">
                    <BarChart3 className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      No budgets configured.{' '}
                      <Link to="/app/personal/budgets" className="text-sapling-primary-100 hover:underline">
                        Create your first budget
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 overflow-x-auto rounded-card border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-3 text-left font-medium text-muted-foreground">Category</th>
                          <th className="px-4 py-3 text-right font-medium text-muted-foreground">Budget</th>
                          <th className="px-4 py-3 text-right font-medium text-muted-foreground">Spent</th>
                          <th className="px-4 py-3 text-right font-medium text-muted-foreground">Remaining</th>
                          <th className="px-4 py-3 text-left font-medium text-muted-foreground min-w-[140px]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budgets.map(budget => {
                          const limit = parseFloat(budget.limit_amount);
                          const remaining = limit - budget.spent_amount;
                          const pct = limit > 0 ? Math.min((budget.spent_amount / limit) * 100, 100) : 0;
                          const status = getBudgetStatus(budget);
                          return (
                            <tr key={budget.id} className="border-b last:border-b-0 hover:bg-muted/30">
                              <td className="px-4 py-3">
                                <Link
                                  to="/app/personal/transactions"
                                  search={{ categoryId: budget.category_id } as Record<string, string>}
                                  className="flex items-center gap-2 hover:text-sapling-primary-100"
                                >
                                  {budget.category_color && (
                                    <span
                                      className="inline-block h-3 w-3 rounded-full shrink-0"
                                      style={{ backgroundColor: budget.category_color }}
                                    />
                                  )}
                                  <span>{budget.category_name}</span>
                                </Link>
                              </td>
                              <td className="px-4 py-3 text-right">{formatCurrency(limit)}</td>
                              <td className="px-4 py-3 text-right">{formatCurrency(budget.spent_amount)}</td>
                              <td className={`px-4 py-3 text-right ${remaining < 0 ? 'text-sapling-error-100' : ''}`}>
                                {formatCurrency(remaining)}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="h-2 w-full max-w-[100px] rounded-full bg-muted">
                                    <div
                                      className={`h-2 rounded-full transition-all ${getStatusColor(status)}`}
                                      style={{ width: `${Math.min(pct, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-medium ${getStatusTextColor(status)}`}>
                                    {Math.round(pct)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Savings Goals */}
              <section className="mt-8">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-lg font-semibold">Savings Goals</h2>
                  <Link
                    to="/app/personal/savings-goals"
                    className="flex items-center gap-1 text-sm text-sapling-primary-100 hover:underline"
                  >
                    Manage Goals <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                {activeGoals.length === 0 ? (
                  <div className="mt-4 rounded-card border border-dashed bg-muted/50 p-6 text-center">
                    <Target className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      No active savings goals.{' '}
                      <Link to="/app/personal/savings-goals" className="text-sapling-primary-100 hover:underline">
                        Set your first goal
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {activeGoals.map(goal => {
                      const current = parseFloat(goal.current_amount);
                      const target = parseFloat(goal.target_amount);
                      const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0;
                      const remaining = target - current;

                      // Monthly needed calculation
                      let monthlyNeeded: number | null = null;
                      if (goal.target_date && remaining > 0) {
                        const today = new Date();
                        const targetDate = new Date(goal.target_date + 'T00:00:00');
                        const monthsLeft = (targetDate.getFullYear() - today.getFullYear()) * 12
                          + (targetDate.getMonth() - today.getMonth());
                        if (monthsLeft > 0) {
                          monthlyNeeded = remaining / monthsLeft;
                        }
                      }

                      return (
                        <div key={goal.id} className="rounded-card border bg-card p-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium truncate">{goal.name}</h3>
                            <span className="text-xs text-muted-foreground">{Math.round(pct)}%</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-muted">
                            <div
                              className="h-2 rounded-full bg-sapling-primary-100 transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="mt-2 flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {formatCurrency(current)} of {formatCurrency(target)}
                            </span>
                            {monthlyNeeded !== null && (
                              <span className="text-xs text-muted-foreground">
                                {formatCurrency(monthlyNeeded)}/mo needed
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Upcoming Loan Payments — only shown if user has active loans */}
              {activeLoans.length > 0 && (
                <section className="mt-8">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-semibold">Upcoming Payments</h2>
                    <Link
                      to="/app/personal/loans"
                      className="flex items-center gap-1 text-sm text-sapling-primary-100 hover:underline"
                    >
                      Manage Loans <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                  {upcomingLoans.length === 0 ? (
                    <div className="mt-4 rounded-card border border-dashed bg-muted/50 p-6 text-center">
                      <CreditCard className="mx-auto h-8 w-8 text-muted-foreground/50" />
                      <p className="mt-2 text-sm text-muted-foreground">
                        No payment due dates set.{' '}
                        <Link to="/app/personal/loans" className="text-sapling-primary-100 hover:underline">
                          Edit your loans
                        </Link>{' '}
                        to add due dates.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-card border divide-y">
                      {upcomingLoans.map(loan => {
                        const currentDay = new Date().getDate();
                        const fulfilled = isPaymentFulfilled(loan.payment_due_day, loan.last_payment_date);
                        const pastDue = currentDay > loan.payment_due_day! && !fulfilled;
                        const dueDay = loan.payment_due_day!;
                        const now = new Date();
                        const dueDate = pastDue || (currentDay > dueDay)
                          ? new Date(now.getFullYear(), now.getMonth() + 1, dueDay)
                          : new Date(now.getFullYear(), now.getMonth(), dueDay);
                        const dueDateStr = dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                        return (
                          <div key={loan.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                                pastDue ? 'bg-sapling-error-100/10'
                                  : fulfilled ? 'bg-green-500/10'
                                  : 'bg-sapling-primary-100/10'
                              }`}>
                                {pastDue ? (
                                  <AlertTriangle className="h-4 w-4 text-sapling-error-100" />
                                ) : fulfilled ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                ) : (
                                  <CreditCard className="h-4 w-4 text-sapling-primary-100" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-medium text-sm">{loan.name}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {loan.lender && <span>{loan.lender}</span>}
                                  {loan.lender && <span>&middot;</span>}
                                  {fulfilled ? (
                                    <span className="text-green-600 font-medium">Paid this cycle</span>
                                  ) : (
                                    <span className={pastDue ? 'text-sapling-error-100 font-medium' : ''}>
                                      {pastDue ? `Past due (was ${dueDateStr})` : `Due ${dueDateStr}`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <span className="shrink-0 font-medium text-sm">
                              {loan.minimum_payment
                                ? formatCurrency(loan.minimum_payment)
                                : formatCurrency(loan.current_balance)}
                            </span>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between px-4 py-3 bg-muted/30 text-sm font-medium">
                        <span className="text-muted-foreground">Total Outstanding Debt</span>
                        <span>{formatCurrency(totalDebt)}</span>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Recent Transactions */}
              <section className="mt-8">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-lg font-semibold">Recent Transactions</h2>
                  <Link
                    to="/app/personal/transactions"
                    className="flex items-center gap-1 text-sm text-sapling-primary-100 hover:underline"
                  >
                    View All <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                {recentTransactions.length === 0 ? (
                  <div className="mt-4 rounded-card border border-dashed bg-muted/50 p-6 text-center">
                    <FolderOpen className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      No transactions this month.{' '}
                      <Link to="/app/personal/transactions" className="text-sapling-primary-100 hover:underline">
                        Add your first transaction
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 rounded-card border divide-y">
                    {recentTransactions.map(tx => {
                      const isIncome = tx.transaction_type === 'income';
                      return (
                        <div key={tx.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isIncome ? 'bg-sapling-primary-100/10' : 'bg-sapling-error-100/10'}`}>
                              {isIncome ? (
                                <TrendingUp className="h-4 w-4 text-sapling-primary-100" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-sapling-error-100" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-medium text-sm">{tx.description}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{formatDate(tx.transaction_date)}</span>
                                {tx.category_name && (
                                  <>
                                    <span>&middot;</span>
                                    <span className="flex items-center gap-1">
                                      {tx.category_color && (
                                        <span
                                          className="inline-block h-2 w-2 rounded-full"
                                          style={{ backgroundColor: tx.category_color }}
                                        />
                                      )}
                                      {tx.category_name}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className={`shrink-0 font-medium text-sm ${isIncome ? 'text-sapling-primary-100' : 'text-sapling-error-100'}`}>
                            {isIncome ? '+' : '-'}{formatCurrency(tx.amount)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Quick Actions */}
              <section className="mt-8">
                <h2 className="font-display text-lg font-semibold">Quick Actions</h2>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to="/app/personal/transactions"
                    search={{ action: 'add' } as Record<string, string>}
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Transaction
                  </Link>
                  <Link
                    to="/app/personal/budgets"
                    search={{ action: 'add' } as Record<string, string>}
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Budget
                  </Link>
                  <Link
                    to="/app/personal/savings-goals"
                    search={{ action: 'add' } as Record<string, string>}
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Goal
                  </Link>
                  <Link
                    to="/app/personal/loans"
                    search={{ action: 'add' } as Record<string, string>}
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Loan
                  </Link>
                  <Link
                    to="/app/personal/categories"
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <FolderOpen className="h-4 w-4" />
                    Manage Categories
                  </Link>
                  <Link
                    to="/app/personal/budgets"
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <BarChart3 className="h-4 w-4" />
                    Manage Budgets
                  </Link>
                  <Link
                    to="/app/personal/savings-goals"
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Target className="h-4 w-4" />
                    Manage Goals
                  </Link>
                  <Link
                    to="/app/personal/loans"
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Banknote className="h-4 w-4" />
                    Manage Loans
                  </Link>
                  <Link
                    to="/app/personal/reports"
                    className="inline-flex items-center gap-2 rounded-btn border bg-card px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <FileBarChart className="h-4 w-4" />
                    Budget Reports
                  </Link>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
